import React, { useEffect, useState, useRef } from 'react';
import { Button, Col, Form, Row, Table, Spinner, Alert, Card, ProgressBar } from 'react-bootstrap';
import {
  User,
  MapPin,
  GraduationCap,
  UploadCloud,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  RotateCcw,
  FileText,
  AlertCircle,
  Eye,
  ShieldCheck,
  CreditCard,
  Building,
  Calendar,
  Phone,
  Mail,
  Heart
} from 'lucide-react';
import axios from 'axios';
import Preloader from '../components/Preloader/Preloader';
import { useNavigate } from 'react-router-dom';

const config = require('../config.json');

const DRAFT_STORAGE_KEY = 'mduedn_staff_application_draft';

function RegistrationForm(props) {
  const { setActiveKey, dispMsg, setDispMsg, selectedJob, formRef } = props;
  const navigate = useNavigate();

  // Current Step state (1 to 5)
  const [currentStep, setCurrentStep] = useState(1);
  const [sameAsCommunicationAddress, setSameAsCommunicationAddress] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);

  // Core Application Data state (all original field names preserved)
  const [application, setApplication] = useState({
    applicantName: '', postApplied: '', appliedSchool: '', appliedSubject: '', dob: '',
    schoolName: '', appliedSchoolID: '', jobPostingID: '', applicationID: '', applicationStatus: '', staffID: '',
    dateOfBirth: '', gender: '', emailID: '', phoneNumber: '', religion: '', bloodGroup: '', caste: '', casteName: '',
    communityCertNo: '', ug_subject: '', ug_passingyear: '', ug_university: '', ug_mode: '', ug_percentage: '', ug_class: '',
    pg_subject: '', pg_passingyear: '', pg_university: '', pg_mode: '', pg_percentage: '', pg_class: '', bed_subject: '',
    bed_passingyear: '', bed_university: '', bed_mode: '', bed_percentage: '', bed_class: '', o_subject: '', o_passingyear: '',
    o_university: '', o_mode: '', o_percentage: '', o_class: '', c_jobStatus: 'employed', p_teachingDetails: '', p_teachingWhere: '',
    fatherName: '', fatherEducation: '', fatherOccupation: '', fatherIncome: '', motherName: '', motherEducation: '',
    motherOccupation: '', motherIncome: '', maritalStatus: '', siblinginJesuitSchool: '', parentinJesuitSchool: '',
    co_activities: '', otherSkills: '', currentAddress: '', permanentAddress: '', cpincode: '', ppincode: '', terms: false
  });

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loaded, setLoaded] = useState(true);
  const [openJobs, setOpenJobs] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refID, setRefID] = useState('');

  // Attachments State
  const [tenthMarksheet, setTenthMarksheet] = useState(null);
  const [tweelthMarksheet, setTweelthMarksheet] = useState(null);
  const [ugMarksheet, setUGMarksheet] = useState(null);
  const [pgMarksheet, setPGMarksheet] = useState(null);
  const [ugCertificate, setUGCertificate] = useState(null);
  const [pgCertificate, setPGCertificate] = useState(null);
  const [tetCertificate, setTETCertificate] = useState(null);
  const [photoCopy, setPhotoCopy] = useState(null);

  // Previews for photo
  const [photoPreview, setPhotoPreview] = useState(null);

  // PhonePe Async States
  const [errorMessage, setErrorMessage] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [isInitiatingPhonePe, setIsInitiatingPhonePe] = useState(false);
  const [isProcessingOrder, setIsProcessingOrder] = useState(false);
  const [phonePeRes, setPhonePeRes] = useState(null);

  // Fetch Open Jobs on Mount
  useEffect(() => {
    fetchJobDetails();
    restoreDraft();
    //window.scrollTo(0, 0);
  }, [selectedJob]);

  // Auto-Save Draft to LocalStorage whenever application changes
  useEffect(() => {
    if (application.applicantName || application.emailID || application.phoneNumber) {
      try {
        localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(application));
      } catch (e) {
        console.error('Failed to auto-save application draft', e);
      }
    }
  }, [application]);

  // Restore Draft
  const restoreDraft = () => {
    try {
      const saved = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setApplication(prev => ({
          ...prev,
          ...parsed,
          // Preserve selectedJob if passed from parent
          ...(selectedJob ? {
            appliedPost: selectedJob.positionApplied,
            appliedSubject: selectedJob.subject,
            schoolName: selectedJob.schoolName,
            appliedSchoolID: selectedJob.appliedSchoolID,
            jobPostingID: selectedJob.jobPostingID
          } : {})
        }));
        setDraftRestored(true);
      }
    } catch (e) {
      console.warn('Failed to restore draft from localStorage', e);
    }
  };

  const clearDraft = () => {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
    setDraftRestored(false);
    handleReset();
  };

  const fetchJobDetails = async () => {
    try {
      setLoaded(false);
      const clientID = 'MDUEDN';
      const searchValue = 'Active';
      const searchTopic = 'PositionStatus';
      await axios.get(`${config.api.invokeURL}/school/jobs?fetchKey=${searchValue}&fetchOption=${searchTopic}&clientID=${clientID}`, {
        headers: {
          'x-api-key': config.api.key
        }
      }).then((response) => {
        if (response.data && response.data.length > 0) {
          setOpenJobs(response.data);
          const targetJob = selectedJob || response.data[0];
          setApplication(prev => ({
            ...prev,
            appliedPost: targetJob.positionApplied || '',
            appliedSubject: targetJob.subject || '',
            schoolName: targetJob.schoolName || '',
            appliedSchoolID: targetJob.appliedSchoolID || '',
            jobPostingID: targetJob.jobPostingID || ''
          }));
        }
        setLoaded(true);
      }).catch((error) => {
        console.error("Error fetching jobs:", error);
        setLoaded(true);
      });
    } catch (error) {
      console.error("Fetch job error:", error);
      setLoaded(true);
    }
  };

  // Helper date generator (original logic preserved)
  const generateDate = () => {
    const current_datetime = new Date();
    const date_year = current_datetime.getFullYear().toString();
    const date_month = (current_datetime.getMonth() + 1).toString().padStart(2, '0');
    const date_date = current_datetime.getDate().toString().padStart(2, '0');
    return `${date_year}-${date_month}-${date_date}`;
  };

  // Helper application code generator (original logic preserved)
  function getApplicationCode(applicationName) {
    if (!applicationName) return "APPL";
    const words = applicationName.trim().split(/\s+/);
    if (words.length === 1) {
      return words[0].substring(0, 4).toUpperCase();
    }
    return (words[0].substring(0, 2) + words[1].substring(0, 2)).toUpperCase();
  }

  // Helper Unique ID generator (original logic preserved)
  const generateUniqueId = () => {
    const now = new Date();
    const name = application.applicantName;
    const appCode = getApplicationCode(name);
    const yy = now.getFullYear().toString().slice(-2);
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const hh = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');
    const ms = String(now.getMilliseconds()).padStart(3, '0');
    return `${appCode}${yy}${mm}${dd}${hh}${min}${ss}${ms}`;
  };

  // Save tapplicantID cookie (original logic preserved)
  function saveApplicantIdToCookie(tapplicantID) {
    if (typeof document !== 'undefined' && tapplicantID) {
      const expires = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toUTCString();
      document.cookie = `tapplicantID=${encodeURIComponent(tapplicantID)}; expires=${expires}; path=/`;
    }
  }

  const handlePostChange = (inputValue) => {
    const tValue = openJobs.find(job => job.jobPostingID === inputValue);
    if (tValue) {
      setApplication(prev => ({
        ...prev,
        appliedPost: tValue.positionApplied,
        schoolName: tValue.schoolName,
        appliedSchoolID: tValue.appliedSchoolID,
        jobPostingID: tValue.jobPostingID,
        appliedSubject: tValue.subject
      }));
      if (errors.appliedPost) {
        setErrors(prev => ({ ...prev, appliedPost: undefined }));
      }
    }
  };

  const handleSameAddressToggle = (checked) => {
    setSameAsCommunicationAddress(checked);
    if (checked) {
      setApplication(prev => ({
        ...prev,
        permanentAddress: prev.currentAddress,
        ppincode: prev.cpincode
      }));
      setErrors(prev => ({ ...prev, permanentAddress: undefined, ppincode: undefined }));
    }
  };

  // Step-Specific Validation
  const validateStep = (stepNumber) => {
    const newErrors = {};

    if (stepNumber === 1) {
      if (!application.jobPostingID) newErrors.appliedPost = "Please select a job opening / post.";
      if (!application.applicantName || application.applicantName.trim() === "") newErrors.applicantName = "Applicant Name is required.";
      if (!application.dateOfBirth || application.dateOfBirth.trim() === "") newErrors.dateOfBirth = "Date of Birth is required.";
      if (!application.gender || application.gender === "" || application.gender === "Select") newErrors.gender = "Gender is required.";

      if (!application.emailID || application.emailID.trim() === "") {
        newErrors.emailID = "Email Address is required.";
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(application.emailID.trim())) {
          newErrors.emailID = "Please enter a valid email address.";
        }
      }

      if (!application.phoneNumber || application.phoneNumber.trim() === "") {
        newErrors.phoneNumber = "Phone Number is required.";
      } else if (!/^\d{10}$/.test(application.phoneNumber.replace(/\D/g, ''))) {
        newErrors.phoneNumber = "Please enter a valid 10-digit mobile number.";
      }

      if (!application.religion || application.religion === "" || application.religion === "Select") newErrors.religion = "Religion is required.";
      if (!application.bloodGroup || application.bloodGroup.trim() === "") newErrors.bloodGroup = "Blood Group is required.";
      if (!application.caste || application.caste === "" || application.caste === "Select") newErrors.caste = "Caste category is required.";
      if (!application.casteName || application.casteName.trim() === "") newErrors.casteName = "Caste Name is required.";
      if (!application.communityCertNo || application.communityCertNo.trim() === "") newErrors.communityCertNo = "Community Certificate Number is required.";
    }

    if (stepNumber === 2) {
      if (!application.currentAddress || application.currentAddress.trim() === "") newErrors.currentAddress = "Address for Communication is required.";
      if (!application.cpincode || application.cpincode.trim() === "") newErrors.cpincode = "Communication Pincode is required.";
      if (!application.permanentAddress || application.permanentAddress.trim() === "") newErrors.permanentAddress = "Permanent Address is required.";
      if (!application.ppincode || application.ppincode.trim() === "") newErrors.ppincode = "Permanent Pincode is required.";

      if (!application.fatherName || application.fatherName.trim() === "") newErrors.fatherName = "Father's Name is required.";
      if (!application.fatherEducation || application.fatherEducation.trim() === "") newErrors.fatherEducation = "Father's Education is required.";
      if (!application.fatherOccupation || application.fatherOccupation.trim() === "") newErrors.fatherOccupation = "Father's Occupation is required.";
      if (!application.fatherIncome || application.fatherIncome.trim() === "") newErrors.fatherIncome = "Father's Annual Income is required.";

      if (!application.motherName || application.motherName.trim() === "") newErrors.motherName = "Mother's Name is required.";
      if (!application.motherEducation || application.motherEducation.trim() === "") newErrors.motherEducation = "Mother's Education is required.";
      if (!application.motherOccupation || application.motherOccupation.trim() === "") newErrors.motherOccupation = "Mother's Occupation is required.";
      if (!application.motherIncome || application.motherIncome.trim() === "") newErrors.motherIncome = "Mother's Annual Income is required.";

      if (!application.maritalStatus || application.maritalStatus === "" || application.maritalStatus === "Select") newErrors.maritalStatus = "Marital Status is required.";
      if (!application.siblinginJesuitSchool || application.siblinginJesuitSchool === "" || application.siblinginJesuitSchool === "Select") newErrors.siblinginJesuitSchool = "Please specify if sibling is in Jesuit school.";
      if (!application.parentinJesuitSchool || application.parentinJesuitSchool === "" || application.parentinJesuitSchool === "Select") newErrors.parentinJesuitSchool = "Please specify if parent is in Jesuit school.";
    }

    if (stepNumber === 3) {
      // UG Fields
      if (!application.ug_subject || application.ug_subject.trim() === "") newErrors.ug_subject = "UG Main Subject is required.";
      if (!application.ug_passingyear || application.ug_passingyear.trim() === "") newErrors.ug_passingyear = "UG Passing Year is required.";
      if (!application.ug_university || application.ug_university.trim() === "") newErrors.ug_university = "UG University is required.";
      if (!application.ug_mode || application.ug_mode.trim() === "") newErrors.ug_mode = "UG Mode (Regular/Corres.) is required.";
      if (!application.ug_percentage || application.ug_percentage.trim() === "") newErrors.ug_percentage = "UG Percentage is required.";
      if (!application.ug_class || application.ug_class.trim() === "") newErrors.ug_class = "UG Class is required.";

      // PG Fields
      if (!application.pg_subject || application.pg_subject.trim() === "") newErrors.pg_subject = "PG Main Subject is required.";
      if (!application.pg_passingyear || application.pg_passingyear.trim() === "") newErrors.pg_passingyear = "PG Passing Year is required.";
      if (!application.pg_university || application.pg_university.trim() === "") newErrors.pg_university = "PG University is required.";
      if (!application.pg_mode || application.pg_mode.trim() === "") newErrors.pg_mode = "PG Mode is required.";
      if (!application.pg_percentage || application.pg_percentage.trim() === "") newErrors.pg_percentage = "PG Percentage is required.";
      if (!application.pg_class || application.pg_class.trim() === "") newErrors.pg_class = "PG Class is required.";

      // B.Ed / D.T.Ed Fields
      if (!application.bed_subject || application.bed_subject.trim() === "") newErrors.bed_subject = "D.T.Ed / B.Ed Main Subject is required.";
      if (!application.bed_passingyear || application.bed_passingyear.trim() === "") newErrors.bed_passingyear = "Passing Year is required.";
      if (!application.bed_university || application.bed_university.trim() === "") newErrors.bed_university = "University / Board is required.";
      if (!application.bed_mode || application.bed_mode.trim() === "") newErrors.bed_mode = "Mode is required.";
      if (!application.bed_percentage || application.bed_percentage.trim() === "") newErrors.bed_percentage = "Percentage is required.";
      if (!application.bed_class || application.bed_class.trim() === "") newErrors.bed_class = "Class is required.";

      if (!application.co_activities || application.co_activities.trim() === "") newErrors.co_activities = "Co-curricular activities selection is required.";
    }

    if (stepNumber === 4) {
      if (!photoCopy) newErrors.photo_copy = "Applicant Photograph is required (JPG/PNG).";
      if (!tenthMarksheet) newErrors.tenth_marksheet = "10th Marksheet is required.";
      if (!tweelthMarksheet) newErrors.twelth_marksheet = "12th Marksheet is required.";
      if (!ugMarksheet) newErrors.ug_marksheet = "UG Marksheet is required.";
      if (!ugCertificate) newErrors.ug_cert = "UG Degree Certificate is required.";
      if (!pgCertificate) newErrors.pg_cert = "PG Degree Certificate is required.";
      if (!pgMarksheet && application.appliedPost === "P.G.Assistant") newErrors.pg_marksheet = "PG Marksheet is required for P.G. Assistant.";
      if (!tetCertificate && application.appliedPost === "B.T.Assistant") newErrors.tet_cert = "TET Certificate is required for B.T. Assistant.";
    }

    if (stepNumber === 5) {
      if (!application.terms) newErrors.terms = "You must agree to the declaration and terms.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
      // window.scrollTo({ top: formRef?.current?.offsetTop || 0, behavior: 'smooth' });
    } else {
      //window.scrollTo({ top: formRef?.current?.offsetTop || 0, behavior: 'smooth' });
    }
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    //window.scrollTo({ top: formRef?.current?.offsetTop || 0, behavior: 'smooth' });
  };

  // Attachment validation helper (< 2MB, allowed formats)
  const handleAttachments = (event, type) => {
    const file = event.target.files[0];
    if (!file) return;

    const maxSizeBytes = 2 * 1024 * 1024; // 2MB
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];

    if (!allowedTypes.includes(file.type)) {
      alert("Invalid format! Please upload a PDF, JPG, or PNG file.");
      event.target.value = '';
      return;
    }

    if (file.size > maxSizeBytes) {
      alert(`File size exceeds 2MB limit! Current size: ${(file.size / (1024 * 1024)).toFixed(2)} MB. Please choose a smaller file.`);
      event.target.value = '';
      return;
    }

    if (type === "PHOTO") {
      setPhotoCopy(file);
      setPhotoPreview(URL.createObjectURL(file));
      setErrors(prev => ({ ...prev, photo_copy: undefined }));
    } else if (type === "X MARK") {
      setTenthMarksheet(file);
      setErrors(prev => ({ ...prev, tenth_marksheet: undefined }));
    } else if (type === "XII MARK") {
      setTweelthMarksheet(file);
      setErrors(prev => ({ ...prev, twelth_marksheet: undefined }));
    } else if (type === "UG MARK") {
      setUGMarksheet(file);
      setErrors(prev => ({ ...prev, ug_marksheet: undefined }));
    } else if (type === "PG MARK") {
      setPGMarksheet(file);
      setErrors(prev => ({ ...prev, pg_marksheet: undefined }));
    } else if (type === "UG CERT") {
      setUGCertificate(file);
      setErrors(prev => ({ ...prev, ug_cert: undefined }));
    } else if (type === "PG CERT") {
      setPGCertificate(file);
      setErrors(prev => ({ ...prev, pg_cert: undefined }));
    } else if (type === "TET CERT") {
      setTETCertificate(file);
      setErrors(prev => ({ ...prev, tet_cert: undefined }));
    }
  };

  function getFileExtension(filename) {
    const index = filename.lastIndexOf('.');
    return index !== -1 ? filename.slice(index) : '';
  }

  // Presigned S3 Upload helper (original logic preserved)
  const postOnS3 = async (staffID, schoolID, file, fileName) => {
    try {
      const fileType = file.type;
      const tfileName = file.name;
      const extension = getFileExtension(tfileName);
      const newFileName = fileName + extension;
      const blob = file.slice(0, file.size, fileType);
      const newFile = new File([blob], newFileName, { type: fileType });
      const bucketName = config.s3.bucketName;
      const keyName = `staff/${schoolID}/${staffID}/profile`;

      const response = await axios.get(
        `${config.api.invokeURL}/image/presigned/uploader?fileName=${newFileName}&bucketName=${bucketName}&fileType=${fileType}&keyName=${keyName}`,
        {
          headers: { 'x-api-key': config.api.key }
        }
      );

      await fetch(response.data.uploadURL, {
        method: 'PUT',
        headers: { "Content-Type": fileType },
        body: newFile
      });
      return "Success";
    } catch (error) {
      console.error("S3 Upload error:", error);
      return "Failed";
    }
  };

  // Form Reset
  const handleReset = () => {
    setApplication({
      applicantName: '', postApplied: '', appliedSchool: '', dob: '',
      jobPostingID: '', applicationID: '', applicationStatus: '', staffID: '', appliedSchoolID: '', appliedSubject: '', schoolName: '',
      dateOfBirth: '', gender: '', emailID: '', phoneNumber: '', religion: '', bloodGroup: '', caste: '', casteName: '',
      communityCertNo: '', ug_subject: '', ug_passingyear: '', ug_university: '', ug_mode: '', ug_percentage: '', ug_class: '',
      pg_subject: '', pg_passingyear: '', pg_university: '', pg_mode: '', pg_percentage: '', pg_class: '', bed_subject: '',
      bed_passingyear: '', bed_university: '', bed_mode: '', bed_percentage: '', bed_class: '', o_subject: '', o_passingyear: '',
      o_university: '', o_mode: '', o_percentage: '', o_class: '', c_jobStatus: 'employed', p_teachingDetails: '', p_teachingWhere: '',
      fatherName: '', fatherEducation: '', fatherOccupation: '', fatherIncome: '', motherName: '', motherEducation: '',
      motherOccupation: '', motherIncome: '', maritalStatus: '', siblinginJesuitSchool: '', parentinJesuitSchool: '',
      co_activities: '', otherSkills: '', currentAddress: '', permanentAddress: '', cpincode: '', ppincode: '', terms: false
    });
    setTenthMarksheet(null);
    setTweelthMarksheet(null);
    setUGCertificate(null);
    setUGMarksheet(null);
    setPGCertificate(null);
    setPGMarksheet(null);
    setPhotoCopy(null);
    setPhotoPreview(null);
    setTETCertificate(null);
    setErrors({});
    setCurrentStep(1);
    setSameAsCommunicationAddress(false);
  };

  // Submit Application & Initiate PhonePe Checkout
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting || isInitiatingPhonePe || !loaded) return;

    // Validate Step 5
    if (!validateStep(5)) {
      return;
    }

    // Full validation check
    for (let s = 1; s <= 5; s++) {
      if (!validateStep(s)) {
        setCurrentStep(s);
        return;
      }
    }

    setIsSubmitting(true);
    setDispMsg(null);
    setErrorMessage('');
    setStatusMessage('Saving application details...');

    try {
      setLoaded(false);
      const tapplicantID = generateUniqueId();
      const tdate = generateDate();

      const {
        applicantName, postApplied, appliedSchool, dob, schoolName,
        jobPostingID, appliedSchoolID, appliedPost, appliedSubject,
        dateOfBirth, gender, emailID, phoneNumber, religion, bloodGroup, caste, casteName,
        communityCertNo, ug_subject, ug_passingyear, ug_university, ug_mode, ug_percentage, ug_class,
        pg_subject, pg_passingyear, pg_university, pg_mode, pg_percentage, pg_class, bed_subject,
        bed_passingyear, bed_university, bed_mode, bed_percentage, bed_class, o_subject, o_passingyear,
        o_university, o_mode, o_percentage, o_class, c_jobStatus, p_teachingDetails, p_teachingWhere,
        fatherName, fatherEducation, fatherOccupation, fatherIncome, motherName, motherEducation,
        motherOccupation, motherIncome, maritalStatus, siblinginJesuitSchool, parentinJesuitSchool,
        co_activities, otherSkills, currentAddress, permanentAddress, cpincode, ppincode, terms
      } = application;

      // Prepare File Names
      const tphotoCopy = photoCopy ? `${tapplicantID}${getFileExtension(photoCopy.name)}` : "";
      const ttenthMarksheet = tenthMarksheet ? `${tapplicantID}_tenthmarksheet${getFileExtension(tenthMarksheet.name)}` : "";
      const ttweelthMarksheet = tweelthMarksheet ? `${tapplicantID}_twelthmarksheet${getFileExtension(tweelthMarksheet.name)}` : "";
      const tugMarksheet = ugMarksheet ? `${tapplicantID}_ugmarksheet${getFileExtension(ugMarksheet.name)}` : "";
      const tpgMarksheet = pgMarksheet ? `${tapplicantID}_pgmarksheet${getFileExtension(pgMarksheet.name)}` : "";
      const tugCertificate = ugCertificate ? `${tapplicantID}_ugcert${getFileExtension(ugCertificate.name)}` : "";
      const tpgCertificate = pgCertificate ? `${tapplicantID}_pgcert${getFileExtension(pgCertificate.name)}` : "";
      const ttetCertificate = tetCertificate ? `${tapplicantID}_tetcert${getFileExtension(tetCertificate.name)}` : "";

      const params = {
        applicantName, postApplied, appliedSchool, dob, schoolName,
        jobPostingID, applicationID: tapplicantID, applicationStatus: "Pending Payment", staffID: tapplicantID, appliedSchoolID, appliedPost, appliedSubject,
        dateOfBirth, gender, emailID, phoneNumber, religion, bloodGroup, caste, casteName,
        communityCertNo, ug_subject, ug_passingyear, ug_university, ug_mode, ug_percentage, ug_class,
        pg_subject, pg_passingyear, pg_university, pg_mode, pg_percentage, pg_class, bed_subject,
        bed_passingyear, bed_university, bed_mode, bed_percentage, bed_class, o_subject, o_passingyear,
        o_university, o_mode, o_percentage, o_class, c_jobStatus, p_teachingDetails, p_teachingWhere,
        fatherName, fatherEducation, fatherOccupation, fatherIncome, motherName, motherEducation,
        motherOccupation, motherIncome, maritalStatus, siblinginJesuitSchool, parentinJesuitSchool,
        co_activities, otherSkills, currentAddress, permanentAddress, cpincode, ppincode, terms,
        updatedBy: applicantName, updatedOn: tdate, dateApplied: tdate, photoCopy: tphotoCopy, tenthMarksheet: ttenthMarksheet,
        tweelthMarksheet: ttweelthMarksheet, ugMarksheet: tugMarksheet, pgMarksheet: tpgMarksheet, ugCertificate: tugCertificate,
        pgCertificate: tpgCertificate, tetCertificate: ttetCertificate, jobCategory: "AIDED"
      };

      const response = await axios.post(`${config.api.invokeURL}/staff/applications`, params, {
        headers: {
          'x-api-key': config.api.key
        }
      });

      if (response.status === 201) {
        setStatusMessage('Uploading verification documents securely...');

        // Upload attachments to S3
        if (photoCopy) await postOnS3(tapplicantID, appliedSchoolID, photoCopy, tapplicantID);
        if (tenthMarksheet) await postOnS3(tapplicantID, appliedSchoolID, tenthMarksheet, `${tapplicantID}_tenthmarksheet`);
        if (tweelthMarksheet) await postOnS3(tapplicantID, appliedSchoolID, tweelthMarksheet, `${tapplicantID}_twelthmarksheet`);
        if (ugMarksheet) await postOnS3(tapplicantID, appliedSchoolID, ugMarksheet, `${tapplicantID}_ugmarksheet`);
        if (pgMarksheet) await postOnS3(tapplicantID, appliedSchoolID, pgMarksheet, `${tapplicantID}_pgmarksheet`);
        if (ugCertificate) await postOnS3(tapplicantID, appliedSchoolID, ugCertificate, `${tapplicantID}_ugcert`);
        if (pgCertificate) await postOnS3(tapplicantID, appliedSchoolID, pgCertificate, `${tapplicantID}_pgcert`);
        if (tetCertificate) await postOnS3(tapplicantID, appliedSchoolID, tetCertificate, `${tapplicantID}_tetcert`);

        saveApplicantIdToCookie(tapplicantID);
        setRefID(tapplicantID);
        setSubmitted(true);
        localStorage.removeItem(DRAFT_STORAGE_KEY); // Clear draft on successful submission

        // Initiate PhonePe Payment
        await handlePhonePePayment(params);
      } else {
        const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
        const appIDs = Array.isArray(data) ? data.map(item => item.applicationID) : [];
        setDispMsg(`You have already applied for this position. Reference ID: ${appIDs[0] || 'N/A'}`);
        setIsSubmitting(false);
        setLoaded(true);
      }
    } catch (error) {
      console.error("Submission Error:", error);
      const errTxt = error?.response?.data?.message || error?.message || "An error occurred while submitting your application.";
      setErrorMessage(errTxt);
      setIsSubmitting(false);
      setLoaded(true);
    }
  };

  // PhonePe Checkout Handler (Original API params preserved)
  const handlePhonePePayment = async (appData) => {
    if (isInitiatingPhonePe || isProcessingOrder) return;

    try {
      setErrorMessage('');
      setStatusMessage('Connecting to PhonePe Secure Gateway. Please wait...');
      setIsInitiatingPhonePe(true);

      const apiBase = config?.api?.invokeURL;
      const orderAmount = 200;
      const todayDate = new Date().toISOString();
      const clientID = 'MDUEDN';

      const params = {
        clientID: clientID,
        jobPostingID: appData.jobPostingID,
        orderId: appData.applicationID,
        orderRefId: appData.applicationID,
        referenceId: 'NA',
        orderType: 'New',
        txStatus: appData.applicationStatus,
        paymentMode: 'phonepe',
        txMsg: 'Order created, pending payment',
        txTime: todayDate,
        orderAmount: orderAmount.toString(),
        pocName: appData.applicantName,
        pocEmail: appData.emailID,
        pocPhone: appData.phoneNumber,
        pocAddress: appData.currentAddress,
        transactionDate: todayDate,
        discountAmount: 0,
        discountValue: 0,
        discountCode: 'NA',
        subTotal: orderAmount,
        gstCharges: 0,
        paymentPartner: 'phonepe',
        cart: "Application Fee",
      };

      const res = await axios.post(`${apiBase}/transaction/phonepev2/checkout`, params, {
        headers: { 'x-api-key': config.api.key }
      });

      setPhonePeRes(res.data);

      const tempApp = { ...appData, merchantOrderId: res.data.merchantOrderId, clientID: clientID };
      sessionStorage.setItem('currentApplication', JSON.stringify(tempApp));

      const redirectUrl = res.data?.phonepeResponse?.redirectUrl;
      if (!redirectUrl) {
        throw new Error('PhonePe checkout URL is missing from server response.');
      }

      const callback = async (response) => {
        if (response === 'USER_CANCEL') {
          setErrorMessage('PhonePe payment was cancelled by user. You can complete payment anytime from the Track Application tab.');
          setIsProcessingOrder(false);
          setIsInitiatingPhonePe(false);
          setLoaded(true);
          return;
        }

        if (response === 'CONCLUDED') {
          try {
            setIsProcessingOrder(true);
            setStatusMessage('Payment received! Finalizing verification and redirecting...');
            await handleOrderStatusUpdate(tempApp);
            navigate(`/application/payment-status?orderId=${tempApp.applicationID}&merchantOrderId=${tempApp.merchantOrderId}`);
          } catch (err) {
            setErrorMessage('Payment confirmed, but order status verification timed out. Please check the Track Application tab.');
            setIsProcessingOrder(false);
            setLoaded(true);
          }
        }
      };

      if (window?.PhonePeCheckout?.transact && typeof window.PhonePeCheckout.transact === 'function') {
        window.PhonePeCheckout.transact({ tokenUrl: redirectUrl, callback, type: 'IFRAME' });
        setLoaded(true);
      } else {
        window.location.href = redirectUrl;
      }
    } catch (error) {
      console.error('Error initiating PhonePe payment:', error);
      setErrorMessage(error?.response?.data?.message || error?.message || 'Unable to connect to PhonePe gateway.');
      setIsProcessingOrder(false);
      setIsInitiatingPhonePe(false);
      setLoaded(true);
    }
  };

  const handleOrderStatusUpdate = async (tapplicationData) => {
    if (!tapplicationData?.merchantOrderId) return;
    try {
      const apiBase = config?.api?.invokeURL;
      const res = await axios.get(`${apiBase}/transaction/phonepev2/order-status?merchantOrderId=${tapplicationData.merchantOrderId}`, {
        headers: { 'x-api-key': config.api.key }
      });
      if (res.data && res.data.status) {
        tapplicationData.applicationStatus = res.data.status;
        sessionStorage.setItem('currentApplication', JSON.stringify(tapplicationData));
      }
    } catch (err) {
      console.error('Error updating order status:', err);
    }
  };

  // Steps Definition for UI Stepper
  const steps = [
    { id: 1, label: 'Position & Personal', icon: User },
    { id: 2, label: 'Address & Family', icon: MapPin },
    { id: 3, label: 'Education & Skills', icon: GraduationCap },
    { id: 4, label: 'Upload Documents', icon: UploadCloud },
    { id: 5, label: 'Review & Pay', icon: CheckCircle }
  ];

  return (
    <div className="py-3 px-2 px-md-4" style={{ minHeight: '100vh', background: '#f8fafc' }} ref={formRef}>
      {!loaded && <Preloader show={true} />}

      <div className="mx-auto" style={{ maxWidth: '1100px' }}>

        {/* Header Title */}
        <div className="text-center mb-4">
          <h2 className="fw-bold text-dark mb-1">Staff Recruitment Application</h2>
          <p className="text-muted small">Madurai Province Catholic Schools Educational Commission (Aided Staff Post)</p>
        </div>

        {/* Draft Notification Banner */}
        {draftRestored && (
          <Alert variant="info" className="d-flex justify-content-between align-items-center py-2 px-3 rounded-3 shadow-sm mb-4">
            <div className="d-flex align-items-center gap-2 small">
              <RotateCcw size={16} className="text-primary" />
              <span>We restored your unsaved application draft from your previous session.</span>
            </div>
            <Button variant="outline-danger" size="sm" className="py-0 px-2" onClick={clearDraft}>
              Discard Draft
            </Button>
          </Alert>
        )}

        {/* Global Messages */}
        {dispMsg && <Alert variant="info" className="rounded-3 shadow-sm">{dispMsg}</Alert>}
        {errorMessage && <Alert variant="danger" className="rounded-3 shadow-sm">{errorMessage}</Alert>}

        {/* --- STEPPER PROGRESS BAR --- */}
        <Card className="border-0 shadow-sm rounded-4 mb-4 overflow-hidden bg-white">
          <Card.Body className="p-3 p-md-4">
            <div className="d-flex justify-content-between align-items-center position-relative">
              <div
                className="position-absolute top-50 start-0 w-100 bg-light"
                style={{ height: '4px', zIndex: 0, transform: 'translateY(-50%)' }}
              >
                <div
                  className="bg-primary transition-all"
                  style={{
                    height: '100%',
                    width: `${((currentStep - 1) / (steps.length - 1)) * 100}%`,
                    transition: 'width 0.4s ease'
                  }}
                />
              </div>

              {steps.map((step) => {
                const Icon = step.icon;
                const isCompleted = currentStep > step.id;
                const isCurrent = currentStep === step.id;

                return (
                  <div
                    key={step.id}
                    className="d-flex flex-column align-items-center position-relative"
                    style={{ zIndex: 1, cursor: isCompleted ? 'pointer' : 'default' }}
                    onClick={() => isCompleted && setCurrentStep(step.id)}
                  >
                    <div
                      className={`d-flex align-items-center justify-content-center rounded-circle fw-bold ${isCompleted
                          ? 'bg-success text-white shadow-sm'
                          : isCurrent
                            ? 'bg-primary text-white shadow'
                            : 'bg-white border text-muted'
                        }`}
                      style={{
                        width: '42px',
                        height: '42px',
                        fontSize: '0.95rem',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {isCompleted ? <CheckCircle size={20} /> : <Icon size={19} />}
                    </div>
                    <span
                      className={`small mt-2 d-none d-md-block text-center fw-600 ${isCurrent ? 'text-primary fw-bold' : isCompleted ? 'text-dark' : 'text-muted'
                        }`}
                      style={{ fontSize: '0.78rem' }}
                    >
                      {step.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </Card.Body>
        </Card>

        {/* --- MAIN FORM CARD --- */}
        <Form noValidate onSubmit={handleSubmit}>
          <Card className="border-0 shadow-soft rounded-4 bg-white p-3 p-md-4 mb-4">
            <Card.Body className="p-0">

              {/* ========================================================================= */}
              {/* STEP 1: POSITION & PERSONAL DETAILS                                       */}
              {/* ========================================================================= */}
              {currentStep === 1 && (
                <div>
                  <div className="border-bottom pb-3 mb-4 d-flex align-items-center gap-2">
                    <User className="text-primary" size={24} />
                    <div>
                      <h4 className="fw-bold mb-0 text-dark">Position & Personal Information</h4>
                      <p className="text-muted small mb-0">Select the vacancy and enter your personal details</p>
                    </div>
                  </div>

                  {/* Position Selection */}
                  <Row className="g-3 mb-4">
                    <Col xs={12}>
                      <Form.Group controlId="postApplied">
                        <Form.Label className="fw-600 text-dark">
                          Select Vacant Post & School <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          size="lg"
                          value={application.jobPostingID}
                          onChange={e => handlePostChange(e.target.value)}
                          isInvalid={!!errors.appliedPost}
                          className="bg-light border-1 py-2.5 fs-6"
                        >
                          {openJobs.length === 0 && <option value="">Loading active job positions...</option>}
                          {openJobs.map((item, idx) => (
                            <option key={idx} value={item.jobPostingID}>
                              {item.positionApplied} ({item.subject}) — {item.schoolName} [{item.jobPostingID}]
                            </option>
                          ))}
                        </Form.Select>
                        {errors.appliedPost && <Form.Control.Feedback type="invalid">{errors.appliedPost}</Form.Control.Feedback>}
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Personal Fields */}
                  <Row className="g-3">
                    <Col xs={12}>
                      <Form.Group controlId="applicantName">
                        <Form.Label className="fw-600 text-dark">
                          Full Name in English (as per 10th / SSLC Certificate) <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="e.g. JOHN DOE"
                          value={application.applicantName}
                          onChange={e => setApplication({ ...application, applicantName: e.target.value })}
                          isInvalid={!!errors.applicantName}
                          className="py-2"
                        />
                        {errors.applicantName && <Form.Control.Feedback type="invalid">{errors.applicantName}</Form.Control.Feedback>}
                      </Form.Group>
                    </Col>

                    <Col md={4} sm={6}>
                      <Form.Group controlId="dateOfBirth">
                        <Form.Label className="fw-600 text-dark">
                          Date of Birth <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="date"
                          value={application.dateOfBirth}
                          onChange={e => setApplication({ ...application, dateOfBirth: e.target.value })}
                          isInvalid={!!errors.dateOfBirth}
                          className="py-2"
                        />
                        {errors.dateOfBirth && <Form.Control.Feedback type="invalid">{errors.dateOfBirth}</Form.Control.Feedback>}
                      </Form.Group>
                    </Col>

                    <Col md={4} sm={6}>
                      <Form.Group controlId="gender">
                        <Form.Label className="fw-600 text-dark">
                          Gender <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          value={application.gender}
                          onChange={e => setApplication({ ...application, gender: e.target.value })}
                          isInvalid={!!errors.gender}
                          className="py-2"
                        >
                          <option value="Select">Select Gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Transgender">Transgender</option>
                        </Form.Select>
                        {errors.gender && <Form.Control.Feedback type="invalid">{errors.gender}</Form.Control.Feedback>}
                      </Form.Group>
                    </Col>

                    <Col md={4} sm={6}>
                      <Form.Group controlId="bloodGroup">
                        <Form.Label className="fw-600 text-dark">
                          Blood Group <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="e.g. O+ve / B+ve"
                          value={application.bloodGroup}
                          onChange={e => setApplication({ ...application, bloodGroup: e.target.value })}
                          isInvalid={!!errors.bloodGroup}
                          className="py-2"
                        />
                        {errors.bloodGroup && <Form.Control.Feedback type="invalid">{errors.bloodGroup}</Form.Control.Feedback>}
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group controlId="emailID">
                        <Form.Label className="fw-600 text-dark">
                          Email Address <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="email"
                          placeholder="applicant@example.com"
                          value={application.emailID}
                          onChange={e => setApplication({ ...application, emailID: e.target.value })}
                          isInvalid={!!errors.emailID}
                          className="py-2"
                        />
                        {errors.emailID && <Form.Control.Feedback type="invalid">{errors.emailID}</Form.Control.Feedback>}
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group controlId="phoneNumber">
                        <Form.Label className="fw-600 text-dark">
                          Mobile Number (10 Digits) <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="tel"
                          placeholder="9876543210"
                          maxLength={10}
                          value={application.phoneNumber}
                          onChange={e => setApplication({ ...application, phoneNumber: e.target.value.replace(/\D/g, '') })}
                          isInvalid={!!errors.phoneNumber}
                          className="py-2"
                        />
                        {errors.phoneNumber && <Form.Control.Feedback type="invalid">{errors.phoneNumber}</Form.Control.Feedback>}
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group controlId="religion">
                        <Form.Label className="fw-600 text-dark">
                          Religion <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          value={application.religion}
                          onChange={e => setApplication({ ...application, religion: e.target.value })}
                          isInvalid={!!errors.religion}
                          className="py-2"
                        >
                          <option value="">Select Religion</option>
                          <option value="R.C">R.C (Roman Catholic)</option>
                          <option value="Non-R.C">Non-R.C</option>
                        </Form.Select>
                        {errors.religion && <Form.Control.Feedback type="invalid">{errors.religion}</Form.Control.Feedback>}
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group controlId="caste">
                        <Form.Label className="fw-600 text-dark">
                          Caste Category <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          value={application.caste}
                          onChange={e => setApplication({ ...application, caste: e.target.value })}
                          isInvalid={!!errors.caste}
                          className="py-2"
                        >
                          <option value="">Select Category</option>
                          <option value="OC">OC</option>
                          <option value="BC">BC</option>
                          <option value="MBC">MBC</option>
                          <option value="SC">SC</option>
                          <option value="ST">ST</option>
                        </Form.Select>
                        {errors.caste && <Form.Control.Feedback type="invalid">{errors.caste}</Form.Control.Feedback>}
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group controlId="casteName">
                        <Form.Label className="fw-600 text-dark">
                          Sub-Caste Name <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="e.g. Nadar / Mukkuvar"
                          value={application.casteName}
                          onChange={e => setApplication({ ...application, casteName: e.target.value })}
                          isInvalid={!!errors.casteName}
                          className="py-2"
                        />
                        {errors.casteName && <Form.Control.Feedback type="invalid">{errors.casteName}</Form.Control.Feedback>}
                      </Form.Group>
                    </Col>

                    <Col xs={12}>
                      <Form.Group controlId="communityCertNo">
                        <Form.Label className="fw-600 text-dark">
                          Community Certificate Number <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Certificate Reference Number"
                          value={application.communityCertNo}
                          onChange={e => setApplication({ ...application, communityCertNo: e.target.value })}
                          isInvalid={!!errors.communityCertNo}
                          className="py-2"
                        />
                        {errors.communityCertNo && <Form.Control.Feedback type="invalid">{errors.communityCertNo}</Form.Control.Feedback>}
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              )}

              {/* ========================================================================= */}
              {/* STEP 2: ADDRESS & FAMILY BACKGROUND                                       */}
              {/* ========================================================================= */}
              {currentStep === 2 && (
                <div>
                  <div className="border-bottom pb-3 mb-4 d-flex align-items-center gap-2">
                    <MapPin className="text-primary" size={24} />
                    <div>
                      <h4 className="fw-bold mb-0 text-dark">Address & Family Background</h4>
                      <p className="text-muted small mb-0">Provide your contact addresses and parental background</p>
                    </div>
                  </div>

                  {/* Address Section */}
                  <h6 className="fw-bold text-dark border-start border-3 border-primary ps-2 mb-3">Residential Details</h6>
                  <Row className="g-3 mb-4">
                    <Col md={8}>
                      <Form.Group controlId="currentAddress">
                        <Form.Label className="fw-600 text-dark">Address for Communication <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          placeholder="House No, Street, Village/Town, District"
                          value={application.currentAddress}
                          onChange={e => {
                            const val = e.target.value;
                            setApplication(prev => ({
                              ...prev,
                              currentAddress: val,
                              ...(sameAsCommunicationAddress ? { permanentAddress: val } : {})
                            }));
                          }}
                          isInvalid={!!errors.currentAddress}
                        />
                        {errors.currentAddress && <Form.Control.Feedback type="invalid">{errors.currentAddress}</Form.Control.Feedback>}
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group controlId="cpincode">
                        <Form.Label className="fw-600 text-dark">Communication Pincode <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="text"
                          maxLength={6}
                          placeholder="6-digit Pincode"
                          value={application.cpincode}
                          onChange={e => {
                            const val = e.target.value.replace(/\D/g, '');
                            setApplication(prev => ({
                              ...prev,
                              cpincode: val,
                              ...(sameAsCommunicationAddress ? { ppincode: val } : {})
                            }));
                          }}
                          isInvalid={!!errors.cpincode}
                          className="py-2"
                        />
                        {errors.cpincode && <Form.Control.Feedback type="invalid">{errors.cpincode}</Form.Control.Feedback>}
                      </Form.Group>
                    </Col>

                    {/* Checkbox Same Address */}
                    <Col xs={12}>
                      <Form.Check
                        type="checkbox"
                        id="sameAddressCheck"
                        label="Permanent Address is same as Address for Communication"
                        checked={sameAsCommunicationAddress}
                        onChange={e => handleSameAddressToggle(e.target.checked)}
                        className="fw-600 text-primary"
                      />
                    </Col>

                    <Col md={8}>
                      <Form.Group controlId="permanentAddress">
                        <Form.Label className="fw-600 text-dark">Permanent Address <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          placeholder="Permanent residential address"
                          value={application.permanentAddress}
                          disabled={sameAsCommunicationAddress}
                          onChange={e => setApplication({ ...application, permanentAddress: e.target.value })}
                          isInvalid={!!errors.permanentAddress}
                        />
                        {errors.permanentAddress && <Form.Control.Feedback type="invalid">{errors.permanentAddress}</Form.Control.Feedback>}
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group controlId="ppincode">
                        <Form.Label className="fw-600 text-dark">Permanent Pincode <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="text"
                          maxLength={6}
                          placeholder="6-digit Pincode"
                          value={application.ppincode}
                          disabled={sameAsCommunicationAddress}
                          onChange={e => setApplication({ ...application, ppincode: e.target.value.replace(/\D/g, '') })}
                          isInvalid={!!errors.ppincode}
                          className="py-2"
                        />
                        {errors.ppincode && <Form.Control.Feedback type="invalid">{errors.ppincode}</Form.Control.Feedback>}
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Father Details */}
                  <h6 className="fw-bold text-dark border-start border-3 border-primary ps-2 mb-3">Father's Information</h6>
                  <Row className="g-3 mb-4">
                    <Col md={3} sm={6}>
                      <Form.Group controlId="fatherName">
                        <Form.Label className="fw-600 text-dark">Father's Name <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="text"
                          value={application.fatherName}
                          onChange={e => setApplication({ ...application, fatherName: e.target.value })}
                          isInvalid={!!errors.fatherName}
                          className="py-2"
                        />
                        {errors.fatherName && <Form.Control.Feedback type="invalid">{errors.fatherName}</Form.Control.Feedback>}
                      </Form.Group>
                    </Col>
                    <Col md={3} sm={6}>
                      <Form.Group controlId="fatherEducation">
                        <Form.Label className="fw-600 text-dark">Father's Education <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="text"
                          value={application.fatherEducation}
                          onChange={e => setApplication({ ...application, fatherEducation: e.target.value })}
                          isInvalid={!!errors.fatherEducation}
                          className="py-2"
                        />
                        {errors.fatherEducation && <Form.Control.Feedback type="invalid">{errors.fatherEducation}</Form.Control.Feedback>}
                      </Form.Group>
                    </Col>
                    <Col md={3} sm={6}>
                      <Form.Group controlId="fatherOccupation">
                        <Form.Label className="fw-600 text-dark">Father's Occupation <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="text"
                          value={application.fatherOccupation}
                          onChange={e => setApplication({ ...application, fatherOccupation: e.target.value })}
                          isInvalid={!!errors.fatherOccupation}
                          className="py-2"
                        />
                        {errors.fatherOccupation && <Form.Control.Feedback type="invalid">{errors.fatherOccupation}</Form.Control.Feedback>}
                      </Form.Group>
                    </Col>
                    <Col md={3} sm={6}>
                      <Form.Group controlId="fatherIncome">
                        <Form.Label className="fw-600 text-dark">Father's Annual Income <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="e.g. 150000"
                          value={application.fatherIncome}
                          onChange={e => setApplication({ ...application, fatherIncome: e.target.value })}
                          isInvalid={!!errors.fatherIncome}
                          className="py-2"
                        />
                        {errors.fatherIncome && <Form.Control.Feedback type="invalid">{errors.fatherIncome}</Form.Control.Feedback>}
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Mother Details */}
                  <h6 className="fw-bold text-dark border-start border-3 border-primary ps-2 mb-3">Mother's Information</h6>
                  <Row className="g-3 mb-4">
                    <Col md={3} sm={6}>
                      <Form.Group controlId="motherName">
                        <Form.Label className="fw-600 text-dark">Mother's Name <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="text"
                          value={application.motherName}
                          onChange={e => setApplication({ ...application, motherName: e.target.value })}
                          isInvalid={!!errors.motherName}
                          className="py-2"
                        />
                        {errors.motherName && <Form.Control.Feedback type="invalid">{errors.motherName}</Form.Control.Feedback>}
                      </Form.Group>
                    </Col>
                    <Col md={3} sm={6}>
                      <Form.Group controlId="motherEducation">
                        <Form.Label className="fw-600 text-dark">Mother's Education <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="text"
                          value={application.motherEducation}
                          onChange={e => setApplication({ ...application, motherEducation: e.target.value })}
                          isInvalid={!!errors.motherEducation}
                          className="py-2"
                        />
                        {errors.motherEducation && <Form.Control.Feedback type="invalid">{errors.motherEducation}</Form.Control.Feedback>}
                      </Form.Group>
                    </Col>
                    <Col md={3} sm={6}>
                      <Form.Group controlId="motherOccupation">
                        <Form.Label className="fw-600 text-dark">Mother's Occupation <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="text"
                          value={application.motherOccupation}
                          onChange={e => setApplication({ ...application, motherOccupation: e.target.value })}
                          isInvalid={!!errors.motherOccupation}
                          className="py-2"
                        />
                        {errors.motherOccupation && <Form.Control.Feedback type="invalid">{errors.motherOccupation}</Form.Control.Feedback>}
                      </Form.Group>
                    </Col>
                    <Col md={3} sm={6}>
                      <Form.Group controlId="motherIncome">
                        <Form.Label className="fw-600 text-dark">Mother's Annual Income <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="e.g. 50000"
                          value={application.motherIncome}
                          onChange={e => setApplication({ ...application, motherIncome: e.target.value })}
                          isInvalid={!!errors.motherIncome}
                          className="py-2"
                        />
                        {errors.motherIncome && <Form.Control.Feedback type="invalid">{errors.motherIncome}</Form.Control.Feedback>}
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Marital & Jesuit School Connections */}
                  <h6 className="fw-bold text-dark border-start border-3 border-primary ps-2 mb-3">Other Background</h6>
                  <Row className="g-3">
                    <Col md={4}>
                      <Form.Group controlId="maritalStatus">
                        <Form.Label className="fw-600 text-dark">Marital Status <span className="text-danger">*</span></Form.Label>
                        <Form.Select
                          value={application.maritalStatus}
                          onChange={e => setApplication({ ...application, maritalStatus: e.target.value })}
                          isInvalid={!!errors.maritalStatus}
                          className="py-2"
                        >
                          <option value="Select">Select Status</option>
                          <option value="Married">Married</option>
                          <option value="Unmarried">Unmarried</option>
                        </Form.Select>
                        {errors.maritalStatus && <Form.Control.Feedback type="invalid">{errors.maritalStatus}</Form.Control.Feedback>}
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group controlId="siblinginJesuitSchool">
                        <Form.Label className="fw-600 text-dark">Sibling in Jesuit School? <span className="text-danger">*</span></Form.Label>
                        <Form.Select
                          value={application.siblinginJesuitSchool}
                          onChange={e => setApplication({ ...application, siblinginJesuitSchool: e.target.value })}
                          isInvalid={!!errors.siblinginJesuitSchool}
                          className="py-2"
                        >
                          <option value="Select">Select</option>
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                        </Form.Select>
                        {errors.siblinginJesuitSchool && <Form.Control.Feedback type="invalid">{errors.siblinginJesuitSchool}</Form.Control.Feedback>}
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group controlId="parentinJesuitSchool">
                        <Form.Label className="fw-600 text-dark">Parent in Jesuit School? <span className="text-danger">*</span></Form.Label>
                        <Form.Select
                          value={application.parentinJesuitSchool}
                          onChange={e => setApplication({ ...application, parentinJesuitSchool: e.target.value })}
                          isInvalid={!!errors.parentinJesuitSchool}
                          className="py-2"
                        >
                          <option value="Select">Select</option>
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                        </Form.Select>
                        {errors.parentinJesuitSchool && <Form.Control.Feedback type="invalid">{errors.parentinJesuitSchool}</Form.Control.Feedback>}
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              )}

              {/* ========================================================================= */}
              {/* STEP 3: EDUCATIONAL QUALIFICATIONS & EXPERIENCE                           */}
              {/* ========================================================================= */}
              {currentStep === 3 && (
                <div>
                  <div className="border-bottom pb-3 mb-4 d-flex align-items-center gap-2">
                    <GraduationCap className="text-primary" size={24} />
                    <div>
                      <h4 className="fw-bold mb-0 text-dark">Educational Qualifications & Experience</h4>
                      <p className="text-muted small mb-0">Enter your degree details, teaching experience, and skills</p>
                    </div>
                  </div>

                  {/* Qualifications Table */}
                  <div className="table-responsive rounded-3 border mb-4">
                    <Table hover className="align-middle mb-0 text-start" style={{ minWidth: '750px' }}>
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '12%' }}>Course</th>
                          <th style={{ width: '20%' }}>Main Subject <span className="text-danger">*</span></th>
                          <th style={{ width: '12%' }}>Passing Year <span className="text-danger">*</span></th>
                          <th style={{ width: '22%' }}>Board / University <span className="text-danger">*</span></th>
                          <th style={{ width: '14%' }}>Regular/Corres. <span className="text-danger">*</span></th>
                          <th style={{ width: '10%' }}>% Mark <span className="text-danger">*</span></th>
                          <th style={{ width: '10%' }}>Class <span className="text-danger">*</span></th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* UG Row */}
                        <tr>
                          <td className="fw-bold text-primary">UG Degree</td>
                          <td>
                            <Form.Control
                              size="sm"
                              placeholder="e.g. B.Sc Maths"
                              value={application.ug_subject}
                              onChange={e => setApplication({ ...application, ug_subject: e.target.value })}
                              isInvalid={!!errors.ug_subject}
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              placeholder="YYYY"
                              value={application.ug_passingyear}
                              onChange={e => setApplication({ ...application, ug_passingyear: e.target.value })}
                              isInvalid={!!errors.ug_passingyear}
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              placeholder="University Name"
                              value={application.ug_university}
                              onChange={e => setApplication({ ...application, ug_university: e.target.value })}
                              isInvalid={!!errors.ug_university}
                            />
                          </td>
                          <td>
                            <Form.Select
                              size="sm"
                              value={application.ug_mode}
                              onChange={e => setApplication({ ...application, ug_mode: e.target.value })}
                              isInvalid={!!errors.ug_mode}
                            >
                              <option value="">Mode</option>
                              <option value="Regular">Regular</option>
                              <option value="Distance/Corres.">Distance</option>
                            </Form.Select>
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              placeholder="%"
                              value={application.ug_percentage}
                              onChange={e => setApplication({ ...application, ug_percentage: e.target.value })}
                              isInvalid={!!errors.ug_percentage}
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              placeholder="I / II"
                              value={application.ug_class}
                              onChange={e => setApplication({ ...application, ug_class: e.target.value })}
                              isInvalid={!!errors.ug_class}
                            />
                          </td>
                        </tr>

                        {/* PG Row */}
                        <tr>
                          <td className="fw-bold text-primary">PG Degree</td>
                          <td>
                            <Form.Control
                              size="sm"
                              placeholder="e.g. M.Sc Maths"
                              value={application.pg_subject}
                              onChange={e => setApplication({ ...application, pg_subject: e.target.value })}
                              isInvalid={!!errors.pg_subject}
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              placeholder="YYYY"
                              value={application.pg_passingyear}
                              onChange={e => setApplication({ ...application, pg_passingyear: e.target.value })}
                              isInvalid={!!errors.pg_passingyear}
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              placeholder="University Name"
                              value={application.pg_university}
                              onChange={e => setApplication({ ...application, pg_university: e.target.value })}
                              isInvalid={!!errors.pg_university}
                            />
                          </td>
                          <td>
                            <Form.Select
                              size="sm"
                              value={application.pg_mode}
                              onChange={e => setApplication({ ...application, pg_mode: e.target.value })}
                              isInvalid={!!errors.pg_mode}
                            >
                              <option value="">Mode</option>
                              <option value="Regular">Regular</option>
                              <option value="Distance/Corres.">Distance</option>
                            </Form.Select>
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              placeholder="%"
                              value={application.pg_percentage}
                              onChange={e => setApplication({ ...application, pg_percentage: e.target.value })}
                              isInvalid={!!errors.pg_percentage}
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              placeholder="I / II"
                              value={application.pg_class}
                              onChange={e => setApplication({ ...application, pg_class: e.target.value })}
                              isInvalid={!!errors.pg_class}
                            />
                          </td>
                        </tr>

                        {/* D.T.Ed / B.Ed Row */}
                        <tr>
                          <td className="fw-bold text-primary">D.T.Ed / B.Ed</td>
                          <td>
                            <Form.Control
                              size="sm"
                              placeholder="e.g. B.Ed Education"
                              value={application.bed_subject}
                              onChange={e => setApplication({ ...application, bed_subject: e.target.value })}
                              isInvalid={!!errors.bed_subject}
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              placeholder="YYYY"
                              value={application.bed_passingyear}
                              onChange={e => setApplication({ ...application, bed_passingyear: e.target.value })}
                              isInvalid={!!errors.bed_passingyear}
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              placeholder="University Name"
                              value={application.bed_university}
                              onChange={e => setApplication({ ...application, bed_university: e.target.value })}
                              isInvalid={!!errors.bed_university}
                            />
                          </td>
                          <td>
                            <Form.Select
                              size="sm"
                              value={application.bed_mode}
                              onChange={e => setApplication({ ...application, bed_mode: e.target.value })}
                              isInvalid={!!errors.bed_mode}
                            >
                              <option value="">Mode</option>
                              <option value="Regular">Regular</option>
                              <option value="Distance/Corres.">Distance</option>
                            </Form.Select>
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              placeholder="%"
                              value={application.bed_percentage}
                              onChange={e => setApplication({ ...application, bed_percentage: e.target.value })}
                              isInvalid={!!errors.bed_percentage}
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              placeholder="I / II"
                              value={application.bed_class}
                              onChange={e => setApplication({ ...application, bed_class: e.target.value })}
                              isInvalid={!!errors.bed_class}
                            />
                          </td>
                        </tr>

                        {/* Other Row */}
                        <tr>
                          <td className="fw-bold text-muted">Any Other</td>
                          <td>
                            <Form.Control
                              size="sm"
                              placeholder="e.g. M.Phil / Ph.D"
                              value={application.o_subject}
                              onChange={e => setApplication({ ...application, o_subject: e.target.value })}
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              placeholder="YYYY"
                              value={application.o_passingyear}
                              onChange={e => setApplication({ ...application, o_passingyear: e.target.value })}
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              placeholder="Institution"
                              value={application.o_university}
                              onChange={e => setApplication({ ...application, o_university: e.target.value })}
                            />
                          </td>
                          <td>
                            <Form.Select
                              size="sm"
                              value={application.o_mode}
                              onChange={e => setApplication({ ...application, o_mode: e.target.value })}
                            >
                              <option value="">Mode</option>
                              <option value="Regular">Regular</option>
                              <option value="Distance/Corres.">Distance</option>
                            </Form.Select>
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              placeholder="%"
                              value={application.o_percentage}
                              onChange={e => setApplication({ ...application, o_percentage: e.target.value })}
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              placeholder="I / II"
                              value={application.o_class}
                              onChange={e => setApplication({ ...application, o_class: e.target.value })}
                            />
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>

                  {/* Teaching Experience & Work History */}
                  <h6 className="fw-bold text-dark border-start border-3 border-primary ps-2 mb-3">Employment & Experience Details</h6>
                  <Row className="g-3 mb-4">
                    <Col md={4}>
                      <Form.Group controlId="c_jobStatus">
                        <Form.Label className="fw-600 text-dark">Present Occupation</Form.Label>
                        <Form.Select
                          value={application.c_jobStatus}
                          onChange={e => setApplication({ ...application, c_jobStatus: e.target.value })}
                          className="py-2"
                        >
                          <option value="employed">Employed</option>
                          <option value="unemployed">Unemployed</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group controlId="p_teachingDetails">
                        <Form.Label className="fw-600 text-dark">If Teaching, Where?</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="School name / Organization"
                          value={application.p_teachingDetails}
                          onChange={e => setApplication({ ...application, p_teachingDetails: e.target.value })}
                          className="py-2"
                        />
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group controlId="p_teachingWhere">
                        <Form.Label className="fw-600 text-dark">Teaching Experience as in TSR</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="e.g. 3 Years in High School"
                          value={application.p_teachingWhere}
                          onChange={e => setApplication({ ...application, p_teachingWhere: e.target.value })}
                          className="py-2"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Skills & Extra-Curriculars */}
                  <h6 className="fw-bold text-dark border-start border-3 border-primary ps-2 mb-3">Activities & Skills</h6>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group controlId="co_activities">
                        <Form.Label className="fw-600 text-dark">Co-curricular Activities <span className="text-danger">*</span></Form.Label>
                        <Form.Select
                          value={application.co_activities}
                          onChange={e => setApplication({ ...application, co_activities: e.target.value })}
                          isInvalid={!!errors.co_activities}
                          className="py-2"
                        >
                          <option value="">Select Activity</option>
                          <option value="None">None</option>
                          <option value="NSS">NSS</option>
                          <option value="NCC">NCC</option>
                          <option value="LASAC">LASAC</option>
                          <option value="AICUF">AICUF</option>
                          <option value="SPORTS & GAMES">SPORTS & GAMES</option>
                          <option value="FIRST AID COURSE">FIRST AID COURSE</option>
                        </Form.Select>
                        {errors.co_activities && <Form.Control.Feedback type="invalid">{errors.co_activities}</Form.Control.Feedback>}
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group controlId="otherSkills">
                        <Form.Label className="fw-600 text-dark">Other Skills (Optional)</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="e.g. Music, Drama, Web Development, Computer Literacy"
                          value={application.otherSkills}
                          onChange={e => setApplication({ ...application, otherSkills: e.target.value })}
                          className="py-2"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              )}

              {/* ========================================================================= */}
              {/* STEP 4: DOCUMENT ATTACHMENTS                                              */}
              {/* ========================================================================= */}
              {currentStep === 4 && (
                <div>
                  <div className="border-bottom pb-3 mb-4 d-flex align-items-center gap-2">
                    <UploadCloud className="text-primary" size={24} />
                    <div>
                      <h4 className="fw-bold mb-0 text-dark">Attach Verification Documents</h4>
                      <p className="text-muted small mb-0">Upload clean scanned copies (PDF / JPG / PNG, Max 2MB each)</p>
                    </div>
                  </div>

                  <Row className="g-4">
                    {/* Photo Upload with Thumbnail Preview */}
                    <Col md={6}>
                      <Card className="border rounded-3 p-3 bg-light h-100">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <Form.Label className="fw-bold text-dark mb-0">
                            Applicant Photograph <span className="text-danger">*</span>
                          </Form.Label>
                          {photoCopy && <span className="badge bg-success">Uploaded</span>}
                        </div>
                        <div className="d-flex align-items-center gap-3 mt-2">
                          <div
                            className="rounded-3 border d-flex align-items-center justify-content-center bg-white overflow-hidden shadow-sm"
                            style={{ width: '80px', height: '80px', flexShrink: 0 }}
                          >
                            {photoPreview ? (
                              <img src={photoPreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <User size={36} className="text-muted" />
                            )}
                          </div>
                          <div className="flex-grow-1">
                            <Form.Control
                              type="file"
                              accept=".jpg,.jpeg,.png"
                              onChange={e => handleAttachments(e, "PHOTO")}
                              isInvalid={!!errors.photo_copy}
                              className="form-control-sm mb-1"
                            />
                            <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>Passport size photo (JPG/PNG only, &lt; 2MB)</p>
                            {errors.photo_copy && <div className="text-danger small mt-1">{errors.photo_copy}</div>}
                          </div>
                        </div>
                      </Card>
                    </Col>

                    {/* 10th Marksheet */}
                    <Col md={6}>
                      <Card className="border rounded-3 p-3 bg-light h-100">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <Form.Label className="fw-bold text-dark mb-0">
                            Xth / SSLC Marksheet <span className="text-danger">*</span>
                          </Form.Label>
                          {tenthMarksheet && <span className="badge bg-success">Uploaded</span>}
                        </div>
                        <Form.Control
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={e => handleAttachments(e, "X MARK")}
                          isInvalid={!!errors.tenth_marksheet}
                          className="form-control-sm mb-1"
                        />
                        <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>PDF or Image format, &lt; 2MB</p>
                        {tenthMarksheet && <div className="text-primary small mt-1">Selected: {tenthMarksheet.name}</div>}
                        {errors.tenth_marksheet && <div className="text-danger small mt-1">{errors.tenth_marksheet}</div>}
                      </Card>
                    </Col>

                    {/* 12th Marksheet */}
                    <Col md={6}>
                      <Card className="border rounded-3 p-3 bg-light h-100">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <Form.Label className="fw-bold text-dark mb-0">
                            XIIth / HSC Marksheet <span className="text-danger">*</span>
                          </Form.Label>
                          {tweelthMarksheet && <span className="badge bg-success">Uploaded</span>}
                        </div>
                        <Form.Control
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={e => handleAttachments(e, "XII MARK")}
                          isInvalid={!!errors.twelth_marksheet}
                          className="form-control-sm mb-1"
                        />
                        <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>PDF or Image format, &lt; 2MB</p>
                        {tweelthMarksheet && <div className="text-primary small mt-1">Selected: {tweelthMarksheet.name}</div>}
                        {errors.twelth_marksheet && <div className="text-danger small mt-1">{errors.twelth_marksheet}</div>}
                      </Card>
                    </Col>

                    {/* UG Marksheet */}
                    <Col md={6}>
                      <Card className="border rounded-3 p-3 bg-light h-100">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <Form.Label className="fw-bold text-dark mb-0">
                            UG Consolidated Marksheet <span className="text-danger">*</span>
                          </Form.Label>
                          {ugMarksheet && <span className="badge bg-success">Uploaded</span>}
                        </div>
                        <Form.Control
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={e => handleAttachments(e, "UG MARK")}
                          isInvalid={!!errors.ug_marksheet}
                          className="form-control-sm mb-1"
                        />
                        <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>PDF or Image format, &lt; 2MB</p>
                        {ugMarksheet && <div className="text-primary small mt-1">Selected: {ugMarksheet.name}</div>}
                        {errors.ug_marksheet && <div className="text-danger small mt-1">{errors.ug_marksheet}</div>}
                      </Card>
                    </Col>

                    {/* UG Certificate */}
                    <Col md={6}>
                      <Card className="border rounded-3 p-3 bg-light h-100">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <Form.Label className="fw-bold text-dark mb-0">
                            UG Degree Certificate <span className="text-danger">*</span>
                          </Form.Label>
                          {ugCertificate && <span className="badge bg-success">Uploaded</span>}
                        </div>
                        <Form.Control
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={e => handleAttachments(e, "UG CERT")}
                          isInvalid={!!errors.ug_cert}
                          className="form-control-sm mb-1"
                        />
                        <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>PDF or Image format, &lt; 2MB</p>
                        {ugCertificate && <div className="text-primary small mt-1">Selected: {ugCertificate.name}</div>}
                        {errors.ug_cert && <div className="text-danger small mt-1">{errors.ug_cert}</div>}
                      </Card>
                    </Col>

                    {/* PG Certificate */}
                    <Col md={6}>
                      <Card className="border rounded-3 p-3 bg-light h-100">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <Form.Label className="fw-bold text-dark mb-0">
                            PG Degree Certificate <span className="text-danger">*</span>
                          </Form.Label>
                          {pgCertificate && <span className="badge bg-success">Uploaded</span>}
                        </div>
                        <Form.Control
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={e => handleAttachments(e, "PG CERT")}
                          isInvalid={!!errors.pg_cert}
                          className="form-control-sm mb-1"
                        />
                        <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>PDF or Image format, &lt; 2MB</p>
                        {pgCertificate && <div className="text-primary small mt-1">Selected: {pgCertificate.name}</div>}
                        {errors.pg_cert && <div className="text-danger small mt-1">{errors.pg_cert}</div>}
                      </Card>
                    </Col>

                    {/* Conditional: PG Marksheet or TET Certificate */}
                    {application.appliedPost === "P.G.Assistant" ? (
                      <Col md={6}>
                        <Card className="border rounded-3 p-3 bg-light h-100 border-primary">
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <Form.Label className="fw-bold text-dark mb-0">
                              PG Consolidated Marksheet <span className="text-danger">* (Required for PG Asst)</span>
                            </Form.Label>
                            {pgMarksheet && <span className="badge bg-success">Uploaded</span>}
                          </div>
                          <Form.Control
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={e => handleAttachments(e, "PG MARK")}
                            isInvalid={!!errors.pg_marksheet}
                            className="form-control-sm mb-1"
                          />
                          <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>PDF or Image format, &lt; 2MB</p>
                          {pgMarksheet && <div className="text-primary small mt-1">Selected: {pgMarksheet.name}</div>}
                          {errors.pg_marksheet && <div className="text-danger small mt-1">{errors.pg_marksheet}</div>}
                        </Card>
                      </Col>
                    ) : (
                      <Col md={6}>
                        <Card className="border rounded-3 p-3 bg-light h-100 border-primary">
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <Form.Label className="fw-bold text-dark mb-0">
                              TET Qualified Certificate <span className="text-danger">* (Required for BT Asst)</span>
                            </Form.Label>
                            {tetCertificate && <span className="badge bg-success">Uploaded</span>}
                          </div>
                          <Form.Control
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={e => handleAttachments(e, "TET CERT")}
                            isInvalid={!!errors.tet_cert}
                            className="form-control-sm mb-1"
                          />
                          <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>PDF or Image format, &lt; 2MB</p>
                          {tetCertificate && <div className="text-primary small mt-1">Selected: {tetCertificate.name}</div>}
                          {errors.tet_cert && <div className="text-danger small mt-1">{errors.tet_cert}</div>}
                        </Card>
                      </Col>
                    )}
                  </Row>
                </div>
              )}

              {/* ========================================================================= */}
              {/* STEP 5: REVIEW SUMMARY & PHONEPE PAYMENT INITIATION                       */}
              {/* ========================================================================= */}
              {currentStep === 5 && (
                <div>
                  <div className="border-bottom pb-3 mb-4 d-flex align-items-center gap-2">
                    <CheckCircle className="text-success" size={24} />
                    <div>
                      <h4 className="fw-bold mb-0 text-dark">Review Application & Settle Fee</h4>
                      <p className="text-muted small mb-0">Verify all entered information before initiating secure payment</p>
                    </div>
                  </div>

                  {/* Summary Card */}
                  <div className="bg-light rounded-4 p-3 p-md-4 mb-4 border">
                    <h6 className="fw-bold text-primary mb-3">Application Summary</h6>
                    <Row className="g-3">
                      <Col md={6}>
                        <div className="small text-muted">Applied Position</div>
                        <div className="fw-bold text-dark">{application.appliedPost} - {application.appliedSubject}</div>
                      </Col>
                      <Col md={6}>
                        <div className="small text-muted">Target School</div>
                        <div className="fw-bold text-dark">{application.schoolName}</div>
                      </Col>
                      <Col md={4} sm={6}>
                        <div className="small text-muted">Applicant Name</div>
                        <div className="fw-bold text-dark">{application.applicantName}</div>
                      </Col>
                      <Col md={4} sm={6}>
                        <div className="small text-muted">Date of Birth / Gender</div>
                        <div className="fw-bold text-dark">{application.dateOfBirth} ({application.gender})</div>
                      </Col>
                      <Col md={4} sm={6}>
                        <div className="small text-muted">Contact Info</div>
                        <div className="fw-bold text-dark">{application.phoneNumber}</div>
                        <div className="small text-muted">{application.emailID}</div>
                      </Col>
                      <Col md={4} sm={6}>
                        <div className="small text-muted">Religion / Category</div>
                        <div className="fw-bold text-dark">{application.religion} - {application.caste} ({application.casteName})</div>
                      </Col>
                      <Col md={8}>
                        <div className="small text-muted">Communication Address</div>
                        <div className="text-dark small">{application.currentAddress} - {application.cpincode}</div>
                      </Col>
                    </Row>
                  </div>

                  {/* Payment Fee Breakdown */}
                  <Card className="border-0 bg-primary bg-opacity-10 rounded-4 p-3 mb-4">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center gap-2">
                        <CreditCard className="text-primary" size={28} />
                        <div>
                          <h6 className="fw-bold text-dark mb-0">Application Processing Fee</h6>
                          <span className="text-muted small">Instant processing via PhonePe Gateway</span>
                        </div>
                      </div>
                      <div className="text-end">
                        <span className="fs-4 fw-bold text-primary">₹ 200.00</span>
                      </div>
                    </div>
                  </Card>

                  {/* Terms Checkbox */}
                  <Form.Group className="mb-4">
                    <Form.Check
                      id="termsCheck"
                      type="checkbox"
                      checked={application.terms}
                      onChange={e => {
                        setApplication({ ...application, terms: e.target.checked });
                        if (errors.terms && e.target.checked) setErrors({ ...errors, terms: undefined });
                      }}
                      label={
                        <span className="small text-dark">
                          I hereby declare that all information furnished in this application is true and correct to the best of my knowledge. I agree to the recruitment policies and <a href="/terms-of-service" target="_blank" rel="noopener noreferrer">terms and conditions</a>.
                        </span>
                      }
                      isInvalid={!!errors.terms}
                    />
                    {errors.terms && <div className="text-danger small mt-1">{errors.terms}</div>}
                  </Form.Group>

                  {/* Processing Status Feedback */}
                  {(isSubmitting || isInitiatingPhonePe || isProcessingOrder || !loaded) && (
                    <div className="alert alert-info d-flex align-items-center justify-content-center gap-2 rounded-3 mb-3">
                      <Spinner animation="border" size="sm" />
                      <span>{statusMessage || 'Saving your application and preparing PhonePe gateway...'}</span>
                    </div>
                  )}
                </div>
              )}

              {/* ========================================================================= */}
              {/* NAVIGATION BUTTONS                                                        */}
              {/* ========================================================================= */}
              <div className="d-flex justify-content-between align-items-center pt-3 mt-4 border-top">
                {currentStep > 1 ? (
                  <Button
                    type="button"
                    variant="outline-secondary"
                    className="px-4 py-2 rounded-pill d-flex align-items-center gap-2"
                    onClick={handlePrevStep}
                    disabled={isSubmitting || isInitiatingPhonePe || isProcessingOrder}
                  >
                    <ArrowLeft size={16} /> Back
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline-danger"
                    size="sm"
                    className="px-3 py-1.5 rounded-pill d-flex align-items-center gap-1"
                    onClick={handleReset}
                  >
                    <RotateCcw size={14} /> Reset Form
                  </Button>
                )}

                {currentStep < 5 ? (
                  <Button
                    type="button"
                    variant="primary"
                    className="px-4 py-2 rounded-pill d-flex align-items-center gap-2"
                    onClick={handleNextStep}
                  >
                    Save & Continue <ArrowRight size={16} />
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    variant="success"
                    size="lg"
                    className="px-5 py-2.5 rounded-pill shadow d-flex align-items-center gap-2 fw-bold"
                    disabled={isSubmitting || isInitiatingPhonePe || isProcessingOrder || !loaded}
                  >
                    {isSubmitting || isInitiatingPhonePe || isProcessingOrder ? (
                      <>
                        <Spinner animation="border" size="sm" />
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <ShieldCheck size={20} /> Proceed to Pay ₹200 via PhonePe
                      </>
                    )}
                  </Button>
                )}
              </div>

            </Card.Body>
          </Card>
        </Form>

      </div>
    </div>
  );
}

export default RegistrationForm;